import type { ReactNode } from 'react';

import type { StorefrontCategory } from '@/features/storefront/api/storefront';
import { StorefrontImage } from '@/features/storefront/components/StorefrontImage';
import { focalStyle } from '@/features/storefront/focal';
import type { StorefrontTheme } from '@/features/storefront/themeSchema';
import { fixedImageSizes } from '@/lib/imageRenditions';

/**
 * A header department link's small round picture (`header.category_icons`):
 * the category's own photo, or its initial letter on the surface colour when
 * it has none - the category strip's pattern, at link size.
 */
export function CategoryLinkIcon({ category }: { category: Pick<StorefrontCategory, 'name' | 'image_url' | 'image_focal'> }) {
  return (
    <span
      data-category-icon=""
      aria-hidden
      className="grid size-6 shrink-0 place-items-center overflow-hidden rounded-full"
      style={{ backgroundColor: 'var(--storefront-surface)' }}
    >
      {category.image_url ? (
        <StorefrontImage
          url={category.image_url}
          sizes={fixedImageSizes(24)}
          alt=""
          className="size-full object-cover"
          style={focalStyle(category.image_focal)}
        />
      ) : (
        <span className="text-[11px] font-semibold uppercase leading-none" style={{ color: 'var(--storefront-muted)' }}>
          {category.name.trim().charAt(0) || '•'}
        </span>
      )}
    </span>
  );
}

/** A department's name, led by its picture when the merchant turned category icons on. */
export function CategoryLinkLabel({
  category,
  header,
}: {
  category: Pick<StorefrontCategory, 'name' | 'image_url' | 'image_focal'>;
  header?: StorefrontTheme['header'];
}): ReactNode {
  if (!header?.category_icons) return category.name;
  return (
    <span className="inline-flex items-center gap-2">
      <CategoryLinkIcon category={category} />
      <span>{category.name}</span>
    </span>
  );
}
